import type { CourseIndex } from '@sw/geo'
import { indexCourse, pointAtChainage } from '@sw/geo'
import type { Emitter, Vec2 } from '@sw/schema'

export interface PathState {
  running: boolean
  /** Distance travelled along the route, in metres. */
  distanceM: number
  direction: 1 | -1
  speedMps: number
  position: Vec2
  velocityMps: Vec2
}

type PathEmitter = Extract<Emitter, { kind: 'path' }>

/**
 * Moves path emitters along their routes.
 *
 * The mixer knows how to place a sound that is moving and shift its pitch as
 * it closes; something still has to decide where it is this instant. That is
 * this: a distance along the route, advanced by the clock, converted to a
 * position and a velocity. The velocity matters as much as the position —
 * without it there is no Doppler, and a helicopter that passes overhead at a
 * constant pitch does not read as passing overhead.
 */
export class PathRunner {
  private readonly indices = new Map<string, CourseIndex>()
  private readonly states = new Map<string, PathState>()

  constructor(emitters: readonly Emitter[]) {
    for (const emitter of emitters) {
      if (emitter.kind !== 'path') continue
      // A route is a course with a corridor wide enough never to matter.
      const index = indexCourse({
        id: emitter.id,
        name: emitter.name,
        points: emitter.waypoints,
        corridorM: Number.MAX_SAFE_INTEGER,
      })
      this.indices.set(emitter.id, index)
      this.states.set(emitter.id, {
        running: emitter.startBehavior === 'sessionStart',
        distanceM: 0,
        direction: 1,
        speedMps: speedOf(emitter, index),
        position: index.points[0],
        velocityMps: { x: 0, y: 0 },
      })
    }
  }

  has(emitterId: string): boolean {
    return this.states.has(emitterId)
  }

  stateOf(emitterId: string): PathState | undefined {
    return this.states.get(emitterId)
  }

  start(emitterId: string, speedMps?: number): void {
    const state = this.states.get(emitterId)
    if (!state) return
    state.running = true
    if (speedMps !== undefined && speedMps > 0) state.speedMps = speedMps
  }

  stop(emitterId: string): void {
    const state = this.states.get(emitterId)
    if (state) state.running = false
  }

  restart(emitterId: string, speedMps?: number): void {
    const state = this.states.get(emitterId)
    const index = this.indices.get(emitterId)
    if (!state || !index) return
    state.distanceM = 0
    state.direction = 1
    state.position = index.points[0]
    state.velocityMps = { x: 0, y: 0 }
    this.start(emitterId, speedMps)
  }

  /** Advances every running route. Returns the ids that reached their end. */
  advance(emitters: readonly Emitter[], deltaSeconds: number): string[] {
    const finished: string[] = []
    if (deltaSeconds <= 0) return finished

    for (const emitter of emitters) {
      if (emitter.kind !== 'path') continue
      const state = this.states.get(emitter.id)
      const index = this.indices.get(emitter.id)
      if (!state || !index || !state.running) continue

      const previous = state.position
      state.distanceM += state.speedMps * deltaSeconds * state.direction

      const reachedEnd =
        state.direction === 1 ? state.distanceM >= index.totalM : state.distanceM <= 0
      let wrapped = false

      if (reachedEnd) {
        switch (emitter.traversal) {
          case 'loop':
            // Wrap rather than clamp, so a long tick does not lose distance.
            state.distanceM = ((state.distanceM % index.totalM) + index.totalM) % index.totalM
            wrapped = true
            break
          case 'pingPong':
            state.direction = state.distanceM >= index.totalM ? -1 : 1
            state.distanceM = Math.min(index.totalM, Math.max(0, state.distanceM))
            break
          case 'once':
            state.distanceM = Math.min(index.totalM, Math.max(0, state.distanceM))
            state.running = false
            finished.push(emitter.id)
            break
        }
      }

      state.position = pointAtChainage(index, state.distanceM)

      if (wrapped) {
        // The position has just jumped from the end of the route back to the
        // start. Differencing that gives a velocity of thousands of metres a
        // second pointing backwards, which reads as a violent pitch snap, so
        // the previous velocity is carried across the seam instead.
        continue
      }

      state.velocityMps = {
        x: (state.position.x - previous.x) / deltaSeconds,
        y: (state.position.y - previous.y) / deltaSeconds,
      }
    }

    return finished
  }
}

function speedOf(emitter: PathEmitter, index: CourseIndex): number {
  if (emitter.motion.mode === 'constantSpeed') return emitter.motion.speedMps
  const seconds = emitter.motion.durationMs / 1000
  return seconds > 0 ? index.totalM / seconds : 0
}
